import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import Typography from '../../Generic/Typography';
import { List, ListItem } from '../../Generic/List';
import styles from './ProductList-tw-styles';
import {
  Accordion,
  AccordionPanel,
  AccordionPanelDetails,
  AccordionPanelHeader,
} from '../../Generic/Accordion';
import i18nMessages from '../../../constants/messages/pt-BR';
import { handleFocusTrap } from '../../../utils/ariaHiddenHelper';

class Dropdown extends Component {
  constructor(props) {
    super(props);
    this.state = {
      expand: false,
      ariaLiveMessage: '',
    };
    this.headingRef = React.createRef();
  }

  componentDidMount() {
    document.addEventListener(i18nMessages.click, this.handleOutsideClick, false);
    document.addEventListener('keydown', this.handleKeyDown, false);
  }

  componentWillUnmount() {
    document.removeEventListener(i18nMessages.click, this.handleOutsideClick, false);
    document.removeEventListener('keydown', this.handleKeyDown, false);

    // Ensure PLP background is restored for screen readers if unmounted while expanded
    const { onDropdownToggle } = this.props;
    if (onDropdownToggle) onDropdownToggle(false);
  }

  handleClick = () => {
    const { expand: expandState } = this.state;
    const newExpandState = !expandState;
    const { onDropdownToggle } = this.props;

    this.setState(
      {
        expand: newExpandState,
      },
      () => {
        // Tell parent to hide/show background content for screen readers
        if (onDropdownToggle) onDropdownToggle(newExpandState);

        if (newExpandState) {
          // Focus first list item when dropdown opens
          // Delay increased to 2200ms to allow screen readers to announce button expanded state completely before moving focus
          setTimeout(() => {
            if (this.node) {
              const firstListItem = this.node.querySelector('[role="menuitemradio"][tabindex="0"]');
              if (firstListItem) {
                firstListItem.focus();
              }
            }
          }, 2200);
        }
      }
    );
  };

  handleOutsideClick = (e) => {
    const { expand } = this.state;
    if (!this.node.contains(e.target)) {
      if (expand) {
        this.closeDropDown();
      }
    }
  };

  handleKeyDown = (e) => {
    const { expand } = this.state;
    const currentHeader = this.getHeaderDOMNode();

    // Handle Enter/Space on heading button to toggle dropdown
    if (!expand && currentHeader && e.target === currentHeader) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.handleClick();
        return;
      }
    }

    // Escape closes dropdown and returns focus to heading
    if (e.key === 'Escape' && expand) {
      e.preventDefault();
      this.closeDropDown();
      return;
    }

    // Trap focus within list items when dropdown is expanded
    if (expand && e.key === 'Tab' && this.node) {
      const listItems = Array.from(
        this.node.querySelectorAll('[role="menuitemradio"][tabindex="0"]')
      ).filter((el) => {
        const style = getComputedStyle(el);
        return (
          el.offsetParent !== null &&
          style.display !== 'none' &&
          style.visibility !== 'hidden'
        );
      });

      if (listItems.length === 0) return;

      const firstItem = listItems[0];
      const lastItem = listItems[listItems.length - 1];
      const activeElement = document.activeElement;

      if (e.shiftKey) {
        // Shift+Tab: if on first item, go to last item
        if (activeElement === firstItem) {
          e.preventDefault();
          lastItem.focus();
        }
      } else {
        // Tab: if on last item, go to first item
        if (activeElement === lastItem) {
          e.preventDefault();
          firstItem.focus();
        }
      }
    }
  };

  // Helper safely extracts the underlying HTML DOM node out of the custom Accordion Component
  getHeaderDOMNode = () => {
    const currentRef = this.headingRef.current;
    if (!currentRef) return null;
    
    // If it's already a standard DOM element with a focus function
    if (typeof currentRef.focus === 'function') {
      return currentRef;
    }
    
    // Fallback: If it's a React Component instance, extract its native DOM node
    try {
      return ReactDOM.findDOMNode(currentRef);
    } catch (err) {
      return null;
    }
  };

  focusHeader = () => {
    requestAnimationFrame(() => {
      const targetElement = this.getHeaderDOMNode();
      if (targetElement && typeof targetElement.focus === 'function') {
        targetElement.focus();
      } else if (this.node) {
        // Ultimate fallback: Find the button element inside the wrapper structure
        const headerBtn = this.node.querySelector('[role="button"]');
        if (headerBtn) headerBtn.focus();
      }
    });
  };

  handleSelectOption = (selectedValue) => {
    const { onSortChange } = this.props;
    if (onSortChange) {
      onSortChange(selectedValue);
    }
    this.closeDropDown();
  };

  closeDropDown = () => {
    const { expand } = this.state;
    const { onDropdownToggle } = this.props;

    if (expand) {
      this.setState({ expand: false }, () => {
        // Tell parent to restore background content for screen readers
        if (onDropdownToggle) onDropdownToggle(false);
        this.focusHeader();
      });
    }
  };

  render() {
    const { index, onSortChange, dValues, dObj, value } = this.props;
    const { expand } = this.state;

    return (
      <div
        ref={(node) => {
          this.node = node;
        }}
        className={styles.dropDown.wrapper}
        role={expand ? 'dialog' : undefined}
        aria-modal={expand ? 'true' : undefined}
        aria-label={expand ? 'Menu de ordenação' : undefined}
      >
        <Accordion>
          <div key={index} className={classNames(styles.dropDown.categoryWrapper)}>
            <AccordionPanel key={index} accordionStyle={styles.dropDown}>
              <AccordionPanelHeader
                panelIndex={index}
                handleClick={this.handleClick}
                isExpanded={expand}
                accordionStyle={styles.dropDown}
                expanded={<span className={styles.dropDown.expandedIcon} />}
                collapsed={<span className={styles.dropDown.collapsedIcon} />}
                onblur
                aria-expanded={expand}
                aria-haspopup="listbox"
                aria-controls="sort-dropdown-list"
                ariaLabel={
                  expand
                    ? 'Organizar por, expanded'
                    : `Organizar por, collapsed, ${dObj[value]?.name || ''} selected`
                }
                role="button"
                tabIndex={0}
                buttonRef={this.headingRef}
                onKeyDown={this.handleKeyDown}
              >
                <div className="flex items-center justify-between w-full">
                  <div>
                    <span className={styles.plpConstDiv}>{i18nMessages.plp_constant_sort}</span>
                    <Typography component="div" className={classNames(styles.dropDown.itemTitle)}>
                      <span>{dObj[value] && ' : '}</span>
                      <span>{dObj[value] && dObj[value].name}</span>
                    </Typography>
                  </div>
                  {expand && (
                    <button
                      type="button"
                      className="text-20 font-bold text-grey hover:text-black focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-black"
                      onClick={(e) => {
                        e.stopPropagation();
                        this.closeDropDown();
                      }}
                      aria-label="Fechar menu"
                      tabIndex={-1}
                      aria-hidden="true"
                    >
                      ×
                    </button>
                  )}
                </div>
              </AccordionPanelHeader>

              <AccordionPanelDetails isExpanded={expand} accordionStyle={styles.dropDown}>
                <List
                  id="sort-dropdown-list"
                  className={classNames(styles.dropDown.itemUl)}
                  role="menu"
                >
                  {dValues &&
                    dValues.map((t, i) => {
                      const isSelected = t === value;
                      const ariaLabel = `${dObj[t].name}, ${isSelected ? 'selected' : 'not selected'}`;

                      // Keep your current behavior: all are focusable when expanded
                      // (Your earlier change made only first selectable; leaving this untouched unless you want roving tabindex.)
                      return (
                        <ListItem
                          key={i}
                          title={dObj[t].name}
                          className={classNames(styles.dropDown.itemLi)}
                          tabIndex={expand ? '0' : '-1'}
                          role="menuitemradio"
                          aria-label={ariaLabel}
                          aria-checked={isSelected}
                          onClick={() => this.handleSelectOption(t)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              this.handleSelectOption(t);
                            }
                          }}
                        >
                          <div
                            role="presentation"
                            className={classNames(styles.dropDown.checkboxDiv)}
                          >
                            <span className={classNames(styles.dropDown.itemText)}>{dObj[t].name}</span>
                          </div>
                        </ListItem>
                      );
                    })}
                </List>
              </AccordionPanelDetails>
            </AccordionPanel>
          </div>
        </Accordion>
      </div>
    );
  }
}

Dropdown.propTypes = {
  index: PropTypes.number,
  onSortChange: PropTypes.func.isRequired,
  dValues: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  dObj: PropTypes.oneOfType([PropTypes.object, PropTypes.array]).isRequired,
  value: PropTypes.string.isRequired,
  // NEW: notify parent so it can aria-hide the PLP list while dropdown is open
  onDropdownToggle: PropTypes.func,
};

Dropdown.defaultProps = {
  index: 0,
  onDropdownToggle: undefined,
};

export default observer(Dropdown);